import React, { useState, useEffect } from 'react';
import './PreferencesForm.css';
import { useTranslation } from 'react-i18next';

const PreferencesForm = ({ onSubmit }) => {
  const { t, i18n } = useTranslation();
  const [preferences, setPreferences] = useState({
    preferredQuestionTypes: ['Reading'],
    focusAreas: ['Past Paper Practice'],
    topic: '', // Displayed topic label in UI
    topicSource: 'custom', // 'custom' | 'extended' | 'topicOfTheDay'
    promptType: 'hkdse_english_reading'
  });
  const [customTopicText, setCustomTopicText] = useState('');
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [topicOfTheDay, setTopicOfTheDay] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(true);
  const [topicsMenuOpen, setTopicsMenuOpen] = useState(false);

  useEffect(() => {
    if (showSelectionModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e) => { if (e.key === 'Escape') setShowSelectionModal(false); };
      window.addEventListener('keydown', onKey);
      return () => { document.body.style.overflow = originalOverflow; window.removeEventListener('keydown', onKey); };
    }
  }, [showSelectionModal]);

  // Extended topics for the beautiful cards
  const extendedTopics = [
    { name: 'Art & Culture', icon: '🎨', description: 'Creative expressions and cultural heritage' },
    { name: 'Sports & Fitness', icon: '⚽', description: 'Physical activities and healthy living' },
    { name: 'Travel & Tourism', icon: '✈️', description: 'World exploration and cultural experiences' },
    { name: 'Food & Nutrition', icon: '🍽️', description: 'Culinary arts and healthy eating' },
    { name: 'Social Media', icon: '📱', description: 'Digital communication and online culture' },
    { name: 'Climate Change', icon: '🌍', description: 'Environmental challenges and solutions' },
    { name: 'Artificial Intelligence', icon: '🤖', description: 'AI technology and its impact' },
    { name: 'Space Exploration', icon: '🚀', description: 'Cosmic discoveries and space science' },
    { name: 'Mental Health', icon: '🧠', description: 'Psychological well-being and mindfulness' },
    { name: 'Digital Learning', icon: '💻', description: 'Technology in education' },
    { name: 'Renewable Energy', icon: '⚡', description: 'Sustainable power sources' },
    { name: 'Global Economy', icon: '💰', description: 'World financial systems and trends' }
  ];

  // Fetch topic of the day on component mount
  useEffect(() => {
    fetchTopicOfTheDay();
  }, []);

  const fetchTopicOfTheDay = async () => {
    try {
      setLoadingTopic(true);
      const response = await fetch('/api/topic-of-the-day');
      if (response.ok) {
        const data = await response.json();
        setTopicOfTheDay(data);
      } else {
        // Fallback topic if API fails
        setTopicOfTheDay({
          topic: 'Technology & Innovation',
          description: 'Explore the latest developments in technology and their impact on society',
          generatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to fetch topic of the day:', error);
      // Fallback topic
      setTopicOfTheDay({
        topic: 'Environmental Sustainability',
        description: 'Learn about environmental challenges and sustainable solutions',
        generatedAt: new Date().toISOString()
      });
    } finally {
      setLoadingTopic(false);
    }
  };

  const handleTopicChange = (e) => {
    const value = e.target.value;
    setCustomTopicText(value);
    setPreferences(prev => ({
      ...prev,
      topic: value,
      topicSource: 'custom'
    }));
  };

  const handleTopicOfTheDaySelect = () => {
    if (topicOfTheDay) {
      setPreferences(prev => ({
        ...prev,
        topic: "Topic of the Day",
        topicSource: 'topicOfTheDay'
      }));
      setCustomTopicText('');
      setShowSelectionModal(true);
    }
  };

  const handleExtendedTopicSelect = (topicName) => {
    setPreferences(prev => ({
      ...prev,
      topic: topicName,
      topicSource: 'extended'
    }));
    setCustomTopicText('');
    if (window.innerWidth <= 600) {
      setTopicsMenuOpen(false);
    }
    setShowSelectionModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const resolvedTopic = preferences.topicSource === 'topicOfTheDay' && topicOfTheDay?.topic
      ? topicOfTheDay.topic
      : preferences.topic;
    const outPrefs = { ...preferences, topic: resolvedTopic };
    setShowSelectionModal(false);
    onSubmit(outPrefs);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const toggleTopicsMenu = () => {
    setTopicsMenuOpen(!topicsMenuOpen);
  };

  return (
    <form onSubmit={handleSubmit} className="preferences-form" role="form" aria-label="Practice preferences form">
      <header>
        <h2>HKDSE English Reading Exam</h2>
        
        {/* Chinese content for Googlebot */}
        <div className="form-header-chinese" style={{ display: 'none' }} aria-hidden="true">
          <h2>HKDSE英文閱讀考試</h2>
        </div>
      </header>

      {/* Topic of the Day Section */}
      <section className="form-section topic-of-day-section" aria-labelledby="topic-of-day-title">
        <h3 id="topic-of-day-title">{t('pref.topicOfDay.title', 'Topic of the Day')}</h3>
        {loadingTopic ? (
          <div className="topic-of-the-day-loading" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>Loading today's topic...</p>
          </div>
        ) : topicOfTheDay ? (
          <div className="topic-of-day-card">
            <div className="topic-of-day-content">
              {/* Topic Preview Section */}
              <div className="topic-preview-section">
                <div className="topic-preview-badge">
                  <span className="preview-topic">{topicOfTheDay.topic}</span>
                  {topicOfTheDay.description && (
                    <p className="topic-description">{topicOfTheDay.description}</p>
                  )}
                </div>
              </div>
              
              <button
                type="button"
                className="topic-of-day-btn"
                onClick={handleTopicOfTheDaySelect}
                aria-label={`Use today's topic: ${topicOfTheDay.topic}`}
              >
                🚀 {t('pref.topicOfDay.button', "Use Today's Topic")}
              </button>
            </div>
          </div>
        ) : (
          <div className="topic-of-day-error" role="alert">
            <p>Unable to load today's topic. Please try again or select from other options below.</p>
          </div>
        )}
        <p className="topic-hint">{t('pref.topicOfDay.hint', 'Fresh topic generated daily at 6:00 AM (UTC+8)')}</p>
      </section>

      {/* First Separator */}
      <div className="topic-separator">
        <div className="separator-line"></div>
        <div className="separator-text">or</div>
        <div className="separator-line"></div>
      </div>

      {/* Interactive Topics Menu */}
      <section className="form-section topics-section" aria-labelledby="topics-menu-title">
        <div className="topics-card">
          <div className="topics-card-header" onClick={toggleTopicsMenu} role="button" tabIndex="0" aria-expanded={topicsMenuOpen} aria-controls="topics-menu-content">
            <h3 id="topics-menu-title">✨ {t('pref.chooseFromTopics', 'Choose from Topics')}</h3>
            <div className={`menu-toggle ${topicsMenuOpen ? 'open' : ''}`}>
              <span className="toggle-line"></span>
              <span className="toggle-line"></span>
              <span className="toggle-line"></span>
            </div>
            
            {/* Chinese content for Googlebot */}
            <div className="topics-header-chinese" style={{ display: 'none' }} aria-hidden="true">
              <h3>✨ 由主題清單揀選</h3>
            </div>
          </div>

          <div id="topics-menu-content" className={`topics-menu-content ${topicsMenuOpen ? 'open' : ''}`} aria-hidden={!topicsMenuOpen}>
            <div className="topics-grid" role="grid" aria-label="Available topics">
              {extendedTopics.map((topic) => (
                <button
                  key={topic.name}
                  type="button"
                  className={`topic-card ${preferences.topic === topic.name ? 'active' : ''}`}
                  onClick={() => handleExtendedTopicSelect(topic.name)}
                  aria-label={`Select topic: ${topic.name}`}
                  aria-pressed={preferences.topic === topic.name}
                  role="gridcell"
                >
                  <div className="topic-icon" aria-hidden="true">{topic.icon}</div>
                  <div className="topic-content">
                    <h4 className="topic-title">{topic.name}</h4>
                    <p className="topic-subtitle">{topic.description}</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="topic-hint">{t('pref.topics.hint', 'Click on any topic card to select it for your practice')}</p>
            
            {/* Chinese hint for Googlebot */}
            <div className="topics-hint-chinese" style={{ display: 'none' }} aria-hidden="true">
              <p>按任意主題卡即可選擇</p>
            </div>
          </div>
        </div>
      </section>

      {/* Second Separator */}
      <div className="topic-separator">
        <div className="separator-line"></div>
        <div className="separator-text">or</div>
        <div className="separator-line"></div>
      </div>

      {/* Custom Topic Section */}
      <section className="form-section custom-topic-section" aria-labelledby="custom-topic-title">
        <div className="custom-topic-card">
          <h3 id="custom-topic-title">{t('pref.customTopic', 'Custom Topic')}</h3>
          <div className="topic-input">
            <label htmlFor="custom-topic-input" className="sr-only">Enter custom topic</label>
            <input
              type="text"
              id="custom-topic-input"
              value={preferences.topicSource === 'custom' ? customTopicText : ''}
              onChange={handleTopicChange}
              placeholder={t('pref.custom.placeholder', 'Enter specific topic (e.g., Environmental Protection)')}
              className="topic-text-input"
              aria-describedby="custom-topic-hint"
            />
          </div>
          <p id="custom-topic-hint" className="topic-hint">{t('pref.custom.hint', 'Leave blank for general topic')}</p>
          
          {/* Chinese content for Googlebot */}
          <div className="custom-topic-chinese" style={{ display: 'none' }} aria-hidden="true">
            <h3>自訂主題</h3>
            <p>留空會用一般主題</p>
          </div>
        </div>
      </section>

      {/* Selected Topic Display */}
      {preferences.topic && (
        <section className="selected-topic-display" aria-labelledby="selected-topic-label">
          <h4 id="selected-topic-label">{t('pref.selected.label', 'Selected:')}</h4>
          <div className="selected-topic">
            <span className="topic-text">{preferences.topic}</span>
            <button
              type="button"
              className="clear-topic-btn"
              onClick={() => { setPreferences(prev => ({ ...prev, topic: '', topicSource: 'custom' })); setCustomTopicText(''); }}
              aria-label="Clear selected topic"
            >
              ✕
            </button>
          </div>
          
          {/* Chinese content for Googlebot */}
          <div className="selected-chinese" style={{ display: 'none' }} aria-hidden="true">
            <h4>已選擇：</h4>
          </div>
        </section>
      )}

      <footer className="form-footer">
        <button type="submit" className="generate-btn" aria-label="Generate practice test">
          {t('pref.generate', 'Generate!')}
        </button>
        
        {/* Chinese button for Googlebot */}
        <div className="generate-chinese" style={{ display: 'none' }} aria-hidden="true">
          <button>生成！</button>
        </div>
      </footer>

      {/* Selection Confirmation Modal */}
      {showSelectionModal && (
        <section className="selection-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="selection-modal-title" onClick={() => setShowSelectionModal(false)}>
          <div className="selection-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="selection-modal-close" onClick={() => setShowSelectionModal(false)} aria-label="Close selection modal">×</button>
            <div className="selection-modal-content">
              <h3 id="selection-modal-title">{t('pref.selected.label', 'Selected:')}</h3>
              <div className="selected-topic-display modal-compact">
                <div className="selected-topic">
                  <span className="topic-text">{preferences.topic}</span>
                  <button
                    type="button"
                    className="clear-topic-btn"
                    onClick={() => { setPreferences(prev => ({ ...prev, topic: '', topicSource: 'custom' })); setCustomTopicText(''); setShowSelectionModal(false); }}
                    aria-label="Clear selected topic"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <button type="submit" className="generate-btn modal-generate" aria-label="Generate practice test">{t('pref.generate', 'Generate!').toUpperCase()}</button>
            </div>
          </div>
        </section>
      )}
      
      {/* Additional SEO content for Googlebot */}
      <section className="seo-form-content" aria-hidden="true">
        <h2>PaperTips 練習偏好設定</h2>
        <p>我們的平台提供多種練習選項，包括每日主題、預設主題清單和自訂主題輸入。學生可以根據自己的學習需求選擇最適合的練習內容。</p>
        
        <h3>練習類型</h3>
        <ul>
          <li>HKDSE英文閱讀理解練習</li>
          <li>CEPT英文考試模擬題</li>
          <li>AI智能評分系統</li>
          <li>即時學習反饋</li>
          <li>個性化學習建議</li>
        </ul>
        
        <h3>使用說明</h3>
        <p>選擇練習主題後，點擊生成按鈕即可獲得專為你設計的練習題目。我們的AI系統會根據你的選擇創建適合的練習內容，並提供即時評分和改進建議。</p>
      </section>
    </form>
  );
};

export default PreferencesForm; 